import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import PersonIcon from '@mui/icons-material/Person';
import StoreLogoBadge from './StoreLogoBadge';
import {
    BidForm,
    AuctionClock,
    formatRemaining,
    HIGHLIGHT_DURATION_MS,
    bidSlideIn,
    bidGlowPulse,
    amountPop,
    badgeFade,
} from './BidWidgets';
import request, { productSocketUrl } from '../api';
import { formatDateTime } from '../dateUtils';
import { formatMXN } from '../numberUtils';
import { useImagePreview } from './ImagePreview';

const POLL_MS = 4000;
const RECONNECT_DELAY_MS = 2000;

function LobbyCountdown({ lobbyEndsAt, now, code, upNext, collectionImageUrl }) {
    const { openPreview } = useImagePreview();
    const remainingMs = new Date(lobbyEndsAt).getTime() - now;
    return (
        <Paper sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                La subasta comienza en
            </Typography>
            <Typography variant="h2" sx={{ fontWeight: 700, color: 'primary.main', my: 2 }}>
                {formatRemaining(remainingMs)}
            </Typography>
            {collectionImageUrl && (
                <Box
                    component="img"
                    src={collectionImageUrl}
                    alt="Colección en subasta"
                    onClick={() => openPreview(collectionImageUrl, 'Colección en subasta')}
                    sx={{
                        width: '100%',
                        maxWidth: 480,
                        maxHeight: 360,
                        objectFit: 'cover',
                        borderRadius: 3,
                        display: 'block',
                        mx: 'auto',
                        my: 3,
                        cursor: 'zoom-in',
                    }}
                />
            )}
            {code && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Código de la sala: <strong style={{ letterSpacing: '0.2em' }}>{code}</strong>
                </Typography>
            )}
            {upNext && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Primer producto: {upNext.title}
                </Typography>
            )}
        </Paper>
    );
}

function RoomView({ token, role, fetchPath, headerExtra }) {
    const { openPreview } = useImagePreview();
    const [room, setRoom] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [now, setNow] = useState(Date.now());
    const [liveItem, setLiveItem] = useState(null);
    const [liveBids, setLiveBids] = useState([]);
    const [wsLive, setWsLive] = useState(false);
    const [newBidId, setNewBidId] = useState(null);
    const [pulseKey, setPulseKey] = useState(0);
    const wsRef = useRef(null);
    const highlightTimerRef = useRef(null);

    const loadRoom = useCallback(() => {
        if (!token) return;
        request(fetchPath, { token })
            .then((data) => {
                setRoom(data);
                setErrorMessage('');
            })
            .catch((err) => {
                if (err.message && (err.message.includes('404') || err.message.includes('403'))) {
                    setNotFound(true);
                }
                setErrorMessage(err.message);
            });
    }, [token, fetchPath]);

    useEffect(() => {
        loadRoom();
        const interval = setInterval(loadRoom, POLL_MS);
        return () => clearInterval(interval);
    }, [loadRoom]);

    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!room) return;
        const item = room.items.find((it) => it.id === room.current_item_id) || null;
        setLiveItem(item);
        setLiveBids(room.current_item_bids || []);
    }, [room]);

    useEffect(() => {
        if (!token || !liveItem) return undefined;
        let cancelled = false;
        let reconnectTimer = null;

        function connect() {
            const ws = new WebSocket(productSocketUrl(liveItem.id, token));
            wsRef.current = ws;

            ws.onopen = () => {
                if (!cancelled) setWsLive(true);
            };

            ws.onmessage = (event) => {
                let msg;
                try {
                    msg = JSON.parse(event.data);
                } catch {
                    return;
                }
                if (msg.type === 'bid_placed') {
                    setLiveItem(msg.product);
                    setLiveBids((prev) => {
                        if (prev.some((b) => b.id === msg.bid.id)) return prev;
                        setNewBidId(msg.bid.id);
                        setPulseKey((k) => k + 1);
                        clearTimeout(highlightTimerRef.current);
                        highlightTimerRef.current = setTimeout(() => setNewBidId(null), HIGHLIGHT_DURATION_MS);
                        return [msg.bid, ...prev];
                    });
                }
            };

            ws.onclose = () => {
                if (cancelled) return;
                setWsLive(false);
                reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
            };

            ws.onerror = () => ws.close();
        }

        connect();

        return () => {
            cancelled = true;
            clearTimeout(reconnectTimer);
            clearTimeout(highlightTimerRef.current);
            if (wsRef.current) wsRef.current.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveItem?.id, token]);

    if (notFound) {
        return <Alert severity="error">{errorMessage || 'Esta sala no existe o ya no está disponible.'}</Alert>;
    }

    if (!room) {
        return <Typography color="text.secondary">Cargando sala…</Typography>;
    }

    const lobbyEndsAtMs = new Date(room.lobby_ends_at).getTime();
    const inLobby = now < lobbyEndsAtMs;
    const upNext = room.items.find((it) => new Date(it.termination_time).getTime() > now);
    const finished = !inLobby && !liveItem;

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {room.collection_name || 'Subasta en Vivo'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {room.store_name} · {room.items.length} {room.items.length === 1 ? 'producto' : 'productos'}
                        {typeof room.participant_count === 'number' && ` · ${room.participant_count} en la sala`}
                    </Typography>
                </Box>
                {headerExtra}
            </Stack>

            {errorMessage && !notFound && (
                <Alert severity="warning" sx={{ mb: 2 }}>{errorMessage}</Alert>
            )}

            {inLobby && (
                <LobbyCountdown
                    lobbyEndsAt={room.lobby_ends_at}
                    now={now}
                    code={room.code}
                    upNext={upNext}
                    collectionImageUrl={room.collection_image_url}
                />
            )}

            {!inLobby && liveItem && (
                <Grid container spacing={4}>
                    <Grid item xs={12} md={7}>
                        <Paper sx={{ overflow: 'hidden' }}>
                            {liveItem.image_url && (
                                <Box sx={{ position: 'relative' }}>
                                    <Box
                                        component="img"
                                        src={liveItem.image_url}
                                        alt={liveItem.title}
                                        onClick={() => openPreview(liveItem.image_url, liveItem.title)}
                                        sx={{ width: '100%', maxHeight: 360, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                                    />
                                    <StoreLogoBadge src={room.store_logo_url} alt={room.store_name} />
                                </Box>
                            )}
                            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                                <Typography variant="h6" gutterBottom>{liveItem.title}</Typography>
                                {liveItem.description && (
                                    <Typography sx={{ mb: 2 }} color="text.secondary">{liveItem.description}</Typography>
                                )}
                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                                    {liveItem.current_bid_username && (
                                        <Avatar sx={{ width: 28, height: 28 }}>
                                            <PersonIcon fontSize="small" />
                                        </Avatar>
                                    )}
                                    <Typography
                                        key={pulseKey}
                                        variant="h5"
                                        fontWeight={700}
                                        sx={{ display: 'inline-block', animation: pulseKey ? `${amountPop} 500ms ease-out` : 'none' }}
                                    >
                                        {formatMXN(liveItem.current_bid != null ? liveItem.current_bid : liveItem.starting_price)}
                                        {liveItem.current_bid_username && (
                                            <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1.5 }}>
                                                por {liveItem.current_bid_username}
                                            </Typography>
                                        )}
                                    </Typography>
                                </Stack>

                                {role === 'customer' && (
                                    <BidForm product={liveItem} token={token} onBid={loadRoom} />
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: { xs: 2.5, sm: 3 } }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Typography variant="h6">Historial de Pujas</Typography>
                                <Chip size="small" label={wsLive ? 'En vivo' : 'Reconectando…'} color={wsLive ? 'success' : 'default'} />
                            </Stack>
                            <AuctionClock startTime={liveItem.starts_at} endTime={liveItem.termination_time} />
                            {liveBids.length === 0 && (
                                <Typography color="text.secondary">Aún no hay pujas. ¡Sé el primero!</Typography>
                            )}
                            <List disablePadding>
                                {liveBids.map((bid, i) => {
                                    const isNew = bid.id === newBidId;
                                    return (
                                        <React.Fragment key={bid.id}>
                                            <ListItem
                                                disableGutters
                                                sx={{
                                                    borderRadius: '5px',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    px: isNew ? 1 : 0,
                                                    mx: isNew ? -1 : 0,
                                                    transformOrigin: 'top center',
                                                    ...(isNew && {
                                                        animation: `${bidSlideIn} 420ms cubic-bezier(0.22, 1, 0.36, 1), ${bidGlowPulse} ${HIGHLIGHT_DURATION_MS}ms ease-out`,
                                                    }),
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar src={bid.avatar_url || undefined} sx={{ bgcolor: 'rgba(15,23,42,0.06)' }}>
                                                        {!bid.avatar_url && <PersonIcon fontSize="small" />}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <span>{bid.username}</span>
                                                            {isNew && (
                                                                <Chip
                                                                    label="Nueva puja"
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{
                                                                        height: 18,
                                                                        fontSize: '0.65rem',
                                                                        animation: `${badgeFade} ${HIGHLIGHT_DURATION_MS}ms ease-in-out`,
                                                                    }}
                                                                />
                                                            )}
                                                        </Stack>
                                                    }
                                                    secondary={formatDateTime(bid.created_at)}
                                                />
                                                <Typography fontWeight={700} color="secondary.main">
                                                    {formatMXN(bid.amount)}
                                                </Typography>
                                            </ListItem>
                                            {i < liveBids.length - 1 && <Divider component="li" sx={{ borderColor: 'divider' }} />}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </Paper>

                        {room.items.length > 1 && (
                            <Paper sx={{ p: { xs: 2.5, sm: 3 }, mt: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Próximas publicaciones
                                </Typography>
                                <List disablePadding>
                                    {room.items
                                        .filter((it) => it.id !== liveItem.id && new Date(it.termination_time).getTime() > now)
                                        .map((it) => (
                                            <ListItem key={it.id} disableGutters>
                                                <ListItemText primary={it.title} secondary={`Comienza: ${formatDateTime(it.starts_at)}`} />
                                            </ListItem>
                                        ))}
                                </List>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            )}

            {finished && (
                <Alert severity="info">La subasta en vivo ha finalizado. ¡Gracias por participar!</Alert>
            )}
        </Box>
    );
}

export default RoomView;
